// FILE: src/components/LegacyMessage.js
// Minimal-diff: UI renamed from “Legacy Messages” to “Account Managers”.
// Data model unchanged (still uses `legacyMessages` collection).

import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import {
  collection, addDoc, query, where, orderBy, onSnapshot,
  updateDoc, deleteDoc, doc, serverTimestamp
} from "firebase/firestore";
import Modal from "./ui/Modal";
import "../styles/layout.css";

function defaultForm(user) {
  return {
    uid: user?.uid || "",
    recipient: "",
    email: "",
    message: "",
    includeVault: false,
    delivery: "upon-death",
    deliverAt: "",
    created: null,
  };
}

export default function LegacyMessage({ user, masterPw }) {
  const [messages, setMessages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm(user));
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const textareaRef = useRef();

  // Load messages
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(
      collection(db, "legacyMessages"),
      where("uid", "==", user.uid),
      orderBy("created", "desc")
    );
    return onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [user?.uid]);

  function openForm(msg = null) {
    setShowForm(true);
    if (msg) {
      setForm({
        ...defaultForm(user),
        ...msg,
        deliverAt: msg.deliverAt || "",
      });
      setEditingId(msg.id);
    } else {
      setForm(defaultForm(user));
      setEditingId(null);
    }
    setTimeout(() => textareaRef.current?.focus(), 0);
  }

  function closeForm() {
    setShowForm(false);
    setForm(defaultForm(user));
    setEditingId(null);
  }

  function handleInput(e) {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setUploading(true);
    try {
      const payload = {
        uid: user.uid,
        recipient: form.recipient,
        email: form.email,
        message: form.message,
        includeVault: form.includeVault,
        delivery: form.delivery,
        deliverAt: form.delivery === "scheduled" ? form.deliverAt : "",
        created: editingId ? form.created : serverTimestamp(),
        updated: serverTimestamp(),
        ...(form.includeVault && masterPw ? { masterPw } : {}),
      };
      if (editingId) {
        await updateDoc(doc(db, "legacyMessages", editingId), payload);
      } else {
        await addDoc(collection(db, "legacyMessages"), payload);
      }
      closeForm();
    } catch (err) {
      alert("Failed to save: " + err.message);
    }
    setUploading(false);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await deleteDoc(doc(db, "legacyMessages", id));
    } catch (err) {
      alert("Failed to delete: " + err.message);
    }
  }

  const filtered = messages.filter((m) => {
    const q = search.toLowerCase();
    return (
      (m.recipient || "").toLowerCase().includes(q) ||
      (m.email || "").toLowerCase().includes(q) ||
      (m.message || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="container">
      <div className="header-row">
        {/* Renamed heading */}
        <h2>Account Managers</h2>
        <button className="btn-add" onClick={() => openForm()}>+</button>
      </div>

      <div style={{ marginBottom: 12, display: "flex", gap: 12, alignItems: "center" }}>
        <input
          type="text"
          className="input"
          placeholder="Search account managers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="grid-three-cols">
        {filtered.length === 0 ? (
          <div className="empty-state">No account managers yet.</div>
        ) : (
          filtered.map((msg) => (
            <div className="card" key={msg.id}>
              <div className="tile">
                <div className="tile-main">
                  <span className="tile-title">{msg.recipient}</span>
                  <span className="tile-sub">{msg.email}</span>
                  <span className="tile-sub">
                    {msg.created?.toDate ? msg.created.toDate().toLocaleString() : ""}
                  </span>
                  <span style={{ marginTop: 6 }}>
                    {msg.message.length > 220 ? msg.message.slice(0, 220) + "…" : msg.message}
                  </span>
                  <span className="tile-sub" style={{ marginTop: 6 }}>
                    Delivery:{" "}
                    {msg.delivery === "immediate"
                      ? "Immediately"
                      : msg.delivery === "scheduled"
                      ? `Scheduled: ${msg.deliverAt}`
                      : "Upon death/admin release"}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-main btn-edit" onClick={() => openForm(msg)}>Edit</button>
                <button className="btn btn-danger" onClick={() => handleDelete(msg.id)}>Delete</button>
              </div>
            </div>
          ))
        )}
      </div>

      <Modal
        open={showForm}
        title={editingId ? "Edit Account Manager" : "Add Account Manager"}
        onClose={closeForm}
      >
        <form onSubmit={handleSubmit}>
          <label>Full Name</label>
          <input
            type="text"
            name="recipient"
            className="input"
            value={form.recipient}
            onChange={handleInput}
            required
          />
          <label>Email</label>
          <input
            type="email"
            name="email"
            className="input"
            value={form.email}
            onChange={handleInput}
            required
          />
          <label>Notes</label>
          <textarea
            name="message"
            ref={textareaRef}
            className="input"
            value={form.message}
            onChange={handleInput}
            rows={5}
            required
            placeholder="Add notes or instructions…"
          />
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              name="includeVault"
              checked={form.includeVault}
              onChange={handleInput}
            />
            Attach my Password Vault master password
          </label>

          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
            <label style={{ margin: 0 }}>Delivery:</label>
            <select
              name="delivery"
              className="input"
              value={form.delivery}
              onChange={handleInput}
              style={{ maxWidth: 260 }}
            >
              <option value="upon-death">Upon Death/Admin Approval</option>
              <option value="scheduled">On Specific Date</option>
              <option value="immediate">Immediately</option>
            </select>
            {form.delivery === "scheduled" && (
              <input
                type="date"
                name="deliverAt"
                className="input"
                value={form.deliverAt}
                onChange={handleInput}
                required
                style={{ maxWidth: 200 }}
              />
            )}
          </div>

          <div className="form-actions">
            <button className="btn btn-main" type="submit" disabled={uploading}>
              {editingId ? "Update" : "Add"}
            </button>
            <button type="button" className="btn btn-cancel" onClick={closeForm}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
